import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Icon } from "rhombus-react";
import { getScoreForEntity } from "selectors/PredictiveScoring";
import { PREDICTIVE_SCORE_ENTITY_TYPES } from "reducers/PredictiveScoring";
import { isSocialCreative } from "utils/common";
import { getScoreTier } from "utils/predictiveScoreTiers";

import "./PredictiveScore.css";

const TOOLTIP_TEXT = "Predictive scores shown are for the primary KPI set in library settings.";

function PredictiveScoreHeader() {
	return (
		<span className="predictive-score-header">
			Predictive Score
			<Icon name="fas fa-info-circle" className="predictive-score-header-info" data-tip={TOOLTIP_TEXT} />
		</span>
	);
}

// Builds the quick-preview route for the row's entity, opening the Predictive Score panel by default
// (?showScore=true). Creatives and versions have different preview routes, mirroring the label cells
// (CreativeLabels / CreativeVersionsLabels).
function buildPreviewPath({ entityType, libraryId, creativeId, versionId, useSocialPreviewRoute }) {
	// Versions have a single preview route regardless of social-ness (the version blade resolves
	// social itself), so useSocialPreviewRoute only matters for the creative route below.
	if (entityType === PREDICTIVE_SCORE_ENTITY_TYPES.VERSION) {
		if (!libraryId || !creativeId || !versionId) { return null; }
		return `/library/${libraryId}/creative/${creativeId}/versions/${versionId}/modal/preview-version?showScore=true`;
	}
	if (!libraryId || !creativeId) { return null; }
	const previewType = useSocialPreviewRoute ? "social" : "basic";
	return `/library/${libraryId}/modal/preview-creative/${previewType}/${creativeId}?showScore=true`;
}

function PredictiveScoreCell({ dependentValues }) {
	const entityId = dependentValues?.id;
	const scoreData = useSelector(state => getScoreForEntity(state, entityId));

	if (scoreData?.status === "PROCESSING") {
		return (
			<span className="predictive-score-processing">
				<span className="score-spinner" />
			</span>
		);
	}

	// No score yet: render a blank cell (per design, no dash placeholder).
	if (scoreData?.score === null || scoreData?.score === undefined) {
		return null;
	}

	const tier = getScoreTier(scoreData.score);
	const value = Math.round(scoreData.score);

	// Clicking a score opens the quick preview with the Predictive Score panel shown.
	const previewPath = buildPreviewPath({
		entityType: dependentValues?.entityType,
		libraryId: dependentValues?.libraryId,
		creativeId: dependentValues?.creativeId,
		versionId: dependentValues?.entityType === PREDICTIVE_SCORE_ENTITY_TYPES.VERSION ? entityId : undefined,
		useSocialPreviewRoute: dependentValues?.useSocialPreviewRoute,
	});

	const content = (
		<span
			className={`predictive-score-value ${tier?.colorClass || ""}`}
			title={tier?.label || ""}
		>
			{value}
		</span>
	);

	return previewPath ? <Link to={previewPath}>{content}</Link> : content;
}

export default PredictiveScoreCell;

// Factory: each grid passes its own context (entity type + owning ids) so the cell can build the
// correct creative-vs-version preview link. `creativeId` is only needed for the versions grid.
export function getPredictiveScoreColumn({ entityType, libraryId, creativeId } = {}) {
	return {
		key: "predictiveScore",
		name: "Predictive Score",
		width: 120,
		sortable: false,
		filterable: false,
		cellClass: "predictive-score-cell",
		headerRenderer: <PredictiveScoreHeader />,
		formatter: <PredictiveScoreCell />,
		getRowMetaData(data) {
			const isVersion = entityType === PREDICTIVE_SCORE_ENTITY_TYPES.VERSION;
			return {
				id: data.id,
				entityType,
				// Creatives carry libraryId on the row; versions get it from grid context.
				libraryId: libraryId || data.creativeLibraryId,
				// For versions the owning creativeId comes from grid context; for creatives the row id is the creative.
				creativeId: isVersion ? creativeId : data.id,
				// Only the creative preview has a basic-vs-social route; versions use a single route (the
				// version blade resolves social itself), so this is undefined for versions rather than a
				// misleading `false`.
				useSocialPreviewRoute: isVersion ? undefined : isSocialCreative(data),
			};
		},
	};
}
